import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import networkHandler from "../../../network/networkHandler";
import networkHandlerReporting from "../../../network/networkHandlerReporting";

import { EApiMiddlewareMethods } from "../../../network/networkHandler.types";
import { countObjectProperties, extractFeatureConfigDetails, isInputAnArray, isNull, isValidArrayWithElements, transformHubWithLatLngFromSelectValues, transformHubWithNameAndValueFromSelectValues, transformSelectValues } from "../../../utils/helpers.utils";
import { CommonState, HubLatLngRecord } from "./commonSlice.types";

import AppConstants from "../../../constants";
import FeaturesConstants from "../../../constants/FeaturesContants";
import { storeCountryUrlsInSession } from "utils/urlUtils";
import { storeHeadersFromApi, clearAllHeaders } from "utils/storageUtils";
import { ConfigType, COUNTRY_URL } from "constants/CommonConstants";
import { isEmpty } from "utils/lodash.utils";

export const countryCodeUpdate = createAsyncThunk("Common/CountryCodeUpdate", async ({ countryCode }: { countryCode: any }, { rejectWithValue }) => {
  try {
    const countryRequest = { url: COUNTRY_URL, method: EApiMiddlewareMethods.GET } as any;
    countryRequest.headers = { countryISOCode: countryCode };
    const { data: countryData } = await networkHandlerReporting(countryRequest);
    let countryUrl = countryData?.url?.wings ? countryData.url.wings : countryData?.countryUrl ? countryData.countryUrl : "";
    clearAllHeaders();
    storeHeadersFromApi(countryData?.headers);
    sessionStorage.setItem(AppConstants.COUNTRY_URL, countryUrl);
     // Store all URLs from countryData.url in sessionStorage
     if (countryData?.url && typeof countryData.url === "object") {
        storeCountryUrlsInSession(countryData.url);
     }
  } catch (error: any) {
    return rejectWithValue(error.response.data);
  }
  return countryCode;
});

export const hubCodeUpdate = createAsyncThunk("Common/HubCodeUpdate", (hubCode: string[]) => {
  return hubCode;
});

export const updateMapDirections = createAsyncThunk("Common/MapDirectionsUpdate", (directions: any) => {
  return directions;
});

export const fetchCommonDropdownValues = createAsyncThunk(
  "Common/FetchCommonDropdownValues",
  async (
    {
      countryCode,
    }: {
      countryCode: any;
    },
    { rejectWithValue }
  ) => {
    const params = countryCode ? countryCode : sessionStorage.getItem(AppConstants.COUNTRY_CODE);
    try {
      const request = {
        url: `fulfilment/filter/get/all/${params}/ORDER`,
        method: EApiMiddlewareMethods.GET,
      } as any;
      request.headers = { countryISOCode: params };
      const { data } = await networkHandlerReporting(request);
      return data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchUserDetailsDropdownValues = createAsyncThunk("Common/FetchUserDetailsDropdownValues", async (params, { rejectWithValue }) => {
  try {
    const request = {
      url: `user/hubs`,
      method: EApiMiddlewareMethods.GET,
    } as any;
    const { data } = await networkHandler(request, { isAuth: true });
    return data;
  } catch (error: any) {
    return rejectWithValue(error.response.data);
  }
});

export const fetchCountryConfigDetils = createAsyncThunk("Common/fetchCountryConfigDetils", async ({ activeStatus, countryCode }: any, { rejectWithValue }) => {
  try {
    const requestData = {
      active: activeStatus,
      types: ConfigType,
    };
    
    const request = { url: `/fulfilment/config/fetch-configs`, method: EApiMiddlewareMethods.POST, data:requestData } as any;
    request.headers = { countryISOCode: sessionStorage.getItem(AppConstants.COUNTRY_CODE) };
    const { data } = await networkHandlerReporting(request)
    return data;
  } catch (error: any) {
    return rejectWithValue(error.response.data);
  }
});

export const fetchCountryHubDetailsDetils = createAsyncThunk("Common/fetchCountryHubDetailsDetils", async ({ countryCode, deliveryType = null }: any, { rejectWithValue }) => {
  try {
    let params = countryCode ? countryCode : sessionStorage.getItem(AppConstants.COUNTRY_CODE);
    const request = { url: `fulfilment/hub/country/${params}`, method: EApiMiddlewareMethods.GET } as any;
    if (!isNull(deliveryType)) {
      request.url = `${request.url}?deliveryType=${deliveryType}`;
    }
    const { data } = await networkHandlerReporting(request);
    return data;
  } catch (error: any) {
    return rejectWithValue(error.response.data);
  }
});

export const fetchCwingsReportCountriesList = createAsyncThunk("Common/fetchCwingsReportCountriesList", async (_, { rejectWithValue }) => {
  try {
    const request = { url: `fulfilment/country/all`, method: EApiMiddlewareMethods.GET } as any;
    const { data } = await networkHandlerReporting(request);
    return data;
  } catch (error: any) {
    return rejectWithValue(error.response.data);
  }
});

const initialState: CommonState = {
  loading: false,
  error: "",
  errorCode: "",
  countryCode: sessionStorage.getItem(AppConstants.COUNTRY_CODE) || AppConstants.COUNTRY_OBJ.value,
  hubCode: JSON.parse(sessionStorage.getItem(AppConstants.HUB_CODE) || "[]") || [],
  hubCodeResetTrigger: 0,
  mapRouteDirections: {},
  hubData: {},
  commonDropdowns: { countriesList: [], hubCodeList: [], hubNameList: [], deliverySlotList: [] },
  userDetailsDropdowns: { countriesList: [], hubList: [], hubListWithCodeAndName: [] },
  routeConfigDetails: {},
  driverTripConfigDetails: {},
  pickedUpConfigDetails: [],
  onDemandConfigDetails: [],
  userFeatureConfigDetails: [],
  hubRecordList: {},
  hubRecordListWithCodeAndName: {},
  hubRecordListWithLatLng: {},
  cwingsReportCountriesList: [],
  clubbingConfigDetails: [],
  onDemandConfigDetailsStore: [],
  assignmentAgingConfigDetails: undefined,
  carriersWithTrackingLink: [],
};

const commonSlice = createSlice({
  name: "Common",
  initialState,
  reducers: {
    // Header owns the global Hub selector's own state and only pushes it into
    // this slice's `hubCode` on change; a page can't reset that selection by
    // dispatching `hubCodeUpdate` directly since Header's UI wouldn't reflect
    // it. Bumping this counter lets a page (e.g. "Clear All Filters" on a
    // table) ask Header to reset the Hub dropdown to "Select All".
    triggerHubCodeReset: (state) => {
      state.hubCodeResetTrigger += 1;
    },
  },
  extraReducers: (builders) => {
    builders
      .addCase(fetchCommonDropdownValues.pending, (state) => {
        state.loading = true;
        state.errorCode = "";
        state.error = "";
      })
      .addCase(fetchCommonDropdownValues.fulfilled, (state, action) => {
        const { payload } = action;
        state.loading = false;
        state.errorCode = "";
        state.error = "";
        let hubCodeList: any = {};
        let hubNameList: any = {};
        let deliverySlotList: any = {};
        if (payload.hubMap) {
          Object.keys(payload.hubMap).forEach((country: any) => {
            hubCodeList[country] = transformSelectValues(payload.hubMap[country], "code");
            hubNameList[country] = transformSelectValues(payload.hubMap[country], "value");
          });
        }
        if (payload.deliverySlot) {
          Object.keys(payload.deliverySlot).forEach((country: any) => {
            deliverySlotList[country] = transformSelectValues(payload.deliverySlot[country]);
          });
        }
        state.commonDropdowns = {
          countriesList: transformSelectValues(payload.countries),
          hubCodeList: hubCodeList,
          hubNameList: hubNameList,
          deliverySlotList: deliverySlotList,
        };
      })
      .addCase(fetchCommonDropdownValues.rejected, (state, action) => {
        const errorPayload: any = action.payload;
        state.loading = false;
        state.errorCode = errorPayload ? errorPayload.error : AppConstants.RESPONSE_CONSTANTS.ERROR_CODES.DEFAULT_API_FAILED;
        state.error = errorPayload ? errorPayload.message : AppConstants.RESPONSE_CONSTANTS.DEFAULT_API_FAILED_ERROR_MSG;
      })
      .addCase(fetchUserDetailsDropdownValues.pending, (state) => {
        state.loading = true;
        state.errorCode = "";
        state.error = "";
      })
      .addCase(fetchUserDetailsDropdownValues.fulfilled, (state, action) => {
        const { payload } = action;
        state.loading = false;
        state.errorCode = "";
        state.error = "";
        state.hubData = payload.hubMap || {};
        const hubList: any = {};
        const hubListWithCodeAndName: any = {};
        if (payload.hubMap) {
          Object.keys(payload.hubMap).forEach((country: any) => {
            hubList[country] = transformSelectValues(payload.hubMap[country]);
            hubListWithCodeAndName[country] = transformHubWithNameAndValueFromSelectValues(payload.hubMap[country]);
          });
        }
        state.userDetailsDropdowns = {
          countriesList: transformSelectValues(payload.countries),
          hubList: hubList,
          hubListWithCodeAndName: hubListWithCodeAndName,
        };
      })
      .addCase(fetchUserDetailsDropdownValues.rejected, (state, action) => {
        const errorPayload: any = action.payload;
        state.loading = false;
        state.errorCode = errorPayload ? errorPayload.error : AppConstants.RESPONSE_CONSTANTS.ERROR_CODES.DEFAULT_API_FAILED;
        state.error = errorPayload ? errorPayload.message : AppConstants.RESPONSE_CONSTANTS.DEFAULT_API_FAILED_ERROR_MSG;
      })
      .addCase(countryCodeUpdate.fulfilled, (state, action) => {
        const { payload } = action;
        sessionStorage.setItem(AppConstants.COUNTRY_CODE, payload);
        sessionStorage.setItem(AppConstants.PLANNING_CONSTANTS.HUB_FOR_ROUTES, "");
        state.countryCode = payload;
      })
      .addCase(hubCodeUpdate.fulfilled, (state, action) => {
        const { payload } = action;
        sessionStorage.setItem(AppConstants.HUB_CODE, JSON.stringify(payload));
        state.hubCode = payload;
      })
      .addCase(updateMapDirections.fulfilled, (state, action) => {
        const { payload } = action;
        state.mapRouteDirections = payload;
      })
      /** fetchCountryConfigDetils */
      .addCase(fetchCountryConfigDetils.pending, (state) => {
        state.loading = true;
        state.errorCode = "";
        state.error = "";
        state.routeConfigDetails = {};
        state.driverTripConfigDetails = {};
        // clear so a country with no TRACKING block doesn't inherit the previous country's carriers
        state.carriersWithTrackingLink = [];
      })
      .addCase(fetchCountryConfigDetils.fulfilled, (state, action) => {
        state.loading = false;
        const { payload } = action;
        const countryCode = sessionStorage.getItem(AppConstants.COUNTRY_CODE);
        const config = countryCode ? payload[countryCode] : {};
        if (config?.ROUTE && !isEmpty(config.ROUTE)) {
          state.routeConfigDetails = config.ROUTE;
        }
        if (config?.DRIVER_TRIP && !isEmpty(config.DRIVER_TRIP)) {
          state.driverTripConfigDetails = config.DRIVER_TRIP;
        }
        if (config?.PICKED_UP && !isEmpty(config.PICKED_UP)) {
          state.pickedUpConfigDetails = config.PICKED_UP;
        }
        if (config?.USER_FEATURE && !isEmpty(config.USER_FEATURE)) {
          const featureDetails = extractFeatureConfigDetails(config.USER_FEATURE);
          if (featureDetails) {
            state.userFeatureConfigDetails = featureDetails;
          }
        }
        if (config?.ON_DEMAND_RIDER && !isEmpty(config?.ON_DEMAND_RIDER)) {
          const filteredConfig = config.ON_DEMAND_RIDER.filter((item: any) => item.key === FeaturesConstants.ON_DEMAND_RIDER_EXP_DASHBOARD);
          state.onDemandConfigDetails = filteredConfig;
          const filteredConfigStore = config.ON_DEMAND_RIDER.filter((item: any) => item.key === FeaturesConstants.ON_DEMAND_RIDER_EXP_STORE_DASHBOARD);
          state.onDemandConfigDetailsStore = filteredConfigStore;
        } 
        if (config?.CLUBBING && !isEmpty(config.CLUBBING)) {
          state.clubbingConfigDetails = config.CLUBBING.filter(
            (item: any) => item.key === FeaturesConstants.CLUBBING_ENABLED_HUB_DASHBOARD
          );
          const agingConfigItem = config.CLUBBING.find(
            (item: any) => item.key === FeaturesConstants.INTELLIGENT_CLUBBING_DRIVER_INDICATOR
          );
          if (agingConfigItem?.active && agingConfigItem?.value) {
            try {
              state.assignmentAgingConfigDetails = JSON.parse(agingConfigItem.value);
            } catch {
              state.assignmentAgingConfigDetails = undefined;
            }
          } else {
            // item missing, or present but inactive -> clear any config from a
            // previous fetch instead of leaving a stale value in state
            state.assignmentAgingConfigDetails = undefined;
          }
        }
        if (config?.TRACKING && !isEmpty(config.TRACKING)) {
          const trackingLinkConfigItem = config.TRACKING.find(
            (item: any) => item.key === FeaturesConstants.TRACKING_LINK_ENABLE_DASHBOARD
          );
          if (trackingLinkConfigItem?.active && trackingLinkConfigItem?.value) {
            state.carriersWithTrackingLink = trackingLinkConfigItem.value
              .split(",")
              .map((carrier: string) => carrier.trim())
              .filter((carrier: string) => carrier);
          }
        }
      })
      .addCase(fetchCountryConfigDetils.rejected, (state, action) => {
        const errorPayload: any = action.payload;
        state.loading = false;
        state.errorCode = errorPayload ? errorPayload.error : AppConstants.RESPONSE_CONSTANTS.ERROR_CODES.DEFAULT_API_FAILED;
        state.error = errorPayload ? errorPayload.message : AppConstants.RESPONSE_CONSTANTS.DEFAULT_API_FAILED_ERROR_MSG;
      })
      .addCase(fetchCountryHubDetailsDetils.pending, (state) => {
        state.loading = true;
        state.errorCode = "";
        state.error = "";
      })
      .addCase(fetchCountryHubDetailsDetils.fulfilled, (state, action) => {
        const {
          payload,
          meta: {
            arg: { countryCode },
          },
        } = action;
        if (payload?.results && countObjectProperties(payload.results) > 0) {
          let hubRecordList: any = [];
          let hubRecordListWithCodeAndName: any = [];
          let hubRecordListWithLatLng: any = [];

          if (countryCode && payload?.results[countryCode] && isInputAnArray(payload?.results[countryCode])) {
            hubRecordList = transformSelectValues(payload?.results[countryCode]);
            hubRecordListWithCodeAndName = transformHubWithNameAndValueFromSelectValues(payload?.results[countryCode]);
            hubRecordListWithLatLng = transformHubWithLatLngFromSelectValues(payload?.results[countryCode]);

            state.hubRecordList[countryCode] = hubRecordList;
            state.hubRecordListWithCodeAndName[countryCode] = hubRecordListWithCodeAndName;
            state.hubRecordListWithLatLng[countryCode] = hubRecordListWithLatLng;
          }
        }
        state.loading = false;
      })
      .addCase(fetchCountryHubDetailsDetils.rejected, (state, action) => {
        const errorPayload: any = action.payload;
        state.loading = false;
        state.errorCode = errorPayload ? errorPayload.error : AppConstants.RESPONSE_CONSTANTS.ERROR_CODES.DEFAULT_API_FAILED;
        state.error = errorPayload ? errorPayload.message : AppConstants.RESPONSE_CONSTANTS.DEFAULT_API_FAILED_ERROR_MSG;
      })
      .addCase(fetchCwingsReportCountriesList.pending, (state) => {
        state.loading = true;
        state.errorCode = "";
        state.error = "";
        state.cwingsReportCountriesList = [];
      })
      .addCase(fetchCwingsReportCountriesList.fulfilled, (state, action) => {
        const { payload } = action;
        if (payload && Array.isArray(payload)) {
          state.cwingsReportCountriesList = transformSelectValues(payload);
        }
        state.loading = false;
      })
      .addCase(fetchCwingsReportCountriesList.rejected, (state, action) => {
        const errorPayload: any = action.payload;
        state.loading = false;
        state.errorCode = errorPayload ? errorPayload.error : AppConstants.RESPONSE_CONSTANTS.ERROR_CODES.DEFAULT_API_FAILED;
        state.error = errorPayload ? errorPayload.message : AppConstants.RESPONSE_CONSTANTS.DEFAULT_API_FAILED_ERROR_MSG;
      });
  },
});

export const { triggerHubCodeReset } = commonSlice.actions;

export default commonSlice.reducer;
